import { defineComponent as _defineComponent } from 'vue'
import { IEventTicketTier } from '@/enitites/event/model/types'
import BKInput from '@/components/AppShared/BKInput.vue'
import BKCurrencyInput from '@/components/AppShared/BKCurrencyInput.vue'
import { computed, ref, watch } from 'vue'
import useValidationRules from '@/shared/composables/validation'
import { useAddTier, useDeleteTier, useUpdateTier } from '@/modules/Payment/api/queries'
import { ITierPayload } from '@/modules/Payment/types'
import { BKConfirmationDialog, ModalWrapper } from '@/shared/ui/modals'
import Bugsnag from '@bugsnag/js'


export default /*#__PURE__*/_defineComponent({
  __name: 'EditTicketTierModal',
  props: {
    eventId: null,
    isOpen: { type: Boolean },
    ticketTier: null,
    totalBattles: null
  },
  emits: ["close", "update:ticket-tier"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'close'): void
  (e: 'update:ticket-tier'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  eventId: number
  isOpen: boolean
  ticketTier: IEventTicketTier | null
  totalBattles: number
};





const tierTitle = ref(props.ticketTier ? props.ticketTier.tierTitle : '')
const price = ref(props.ticketTier ? props.ticketTier.price : 0)
const description = ref(props.ticketTier ? props.ticketTier.description : '')
const limit = ref(props.ticketTier?.limit)
const isNew = computed(() => !props.ticketTier?.id)
const isShowDeleteConfirmation = ref(false)

const isFormValid = ref(true)

const rules = useValidationRules()

const { mutate: addTier, isPending: isAddingTier } = useAddTier()
const { mutate: updateTier, isPending: isUpdatingTier } = useUpdateTier()
const { mutate: deleteTier, isPending: isDeletingTier } = useDeleteTier()

const isProcessing = computed(() => isAddingTier.value || isUpdatingTier.value || isDeletingTier.value)

function resetForm() {
  tierTitle.value = ''
  price.value = 0
  description.value = ''
  limit.value = undefined
}

function handleLimitUpdate(value: string | number) {
  if (typeof value === 'string') {
    if (value.trim() === '') {
      limit.value = 1
      return
    }

    const numericValue = parseInt(value, 10)

    if (Number.isNaN(numericValue)) {
      limit.value = 1
      return
    }

    limit.value = Math.max(1, numericValue)
  } else {
    limit.value = Math.max(1, value || 1)
  }
}

function handleSave() {
  if (isProcessing.value) return

  const payload: ITierPayload = {
    tierTitle: tierTitle.value,
    price: price.value,
    description: description.value,
    limit: limit.value,
  }

  const callbacks = {
    onSuccess: () => {
      emit('update:ticket-tier')
      resetForm()
    },
    onError: (err: Error) => {
      Bugsnag.notify(err, (event) => {
        event.addMetadata('TicketTierModal', {
          eventId: props.eventId,
          tierTitle: tierTitle.value,
          price: price.value,
          description: description.value,
          limit: limit.value,
        })
      })
    },
  }

  if (isNew.value) {
    addTier(
      {
        eventId: props.eventId,
        payload,
      },
      callbacks
    )
  } else if (props.ticketTier?.id) {
    updateTier(
      {
        tierId: props.ticketTier.id,
        payload,
      },
      callbacks
    )
  }
}

function handleDelete() {
  if (isProcessing.value || !props.ticketTier?.id) return
  isShowDeleteConfirmation.value = false

  deleteTier(
    {
      tierId: props.ticketTier.id,
      eventId: props.eventId,
    },
    {
      onSuccess: () => {
        emit('update:ticket-tier')
        resetForm()
      },
      onError: (err: Error) => {
        Bugsnag.notify(err, (event) => {
          event.addMetadata('TicketTierModal_Delete', {
            eventId: props.eventId,
            tierId: props.ticketTier?.id,
          })
        })
      },
    }
  )
}

function handleClose() {
  emit('close')
  resetForm()
}

watch(
  () => props.ticketTier,
  (newTier) => {
    if (newTier) {
      tierTitle.value = newTier.tierTitle
      price.value = newTier.price
      description.value = newTier.description
      limit.value = newTier.limit
    } else {
      resetForm()
    }
  },
  { immediate: true }
)

return { __sfc: true,props, emit, tierTitle, price, description, limit, isNew, isShowDeleteConfirmation, isFormValid, rules, addTier, isAddingTier, updateTier, isUpdatingTier, deleteTier, isDeletingTier, isProcessing, resetForm, handleLimitUpdate, handleSave, handleDelete, handleClose, BKInput, BKCurrencyInput, BKConfirmationDialog, ModalWrapper }
}

})